/**
 * UI string translations for the client-facing form.
 * Add new languages here as needed.
 */

export interface UiTranslations {
  // Navigation
  back: string;
  continue: string;
  save: string;
  review: string;
  unableToAnswer: string;
  skipReasonTitle: string;
  skipDocReasonTitle: string;
  skipReasonDescription: string;
  skipReasonPlaceholder: string;
  skipReasonConfirm: string;
  cancel: string;
  saveAndQuit: string;
  continueForm: string;
  poweredBy: string;

  // Top-level a11y labels
  changeLanguageAria: (currentLabel: string) => string;

  // Welcome screen
  hello: string;
  /** Stand-in for the client's first name when the record has none. */
  defaultClientName: string;
  /** Alt text for the firm's logo when it replaces the default badge. */
  firmLogoAlt: string;
  welcomeMessage: (firmName: string) => string;
  autosaveReassurance: string;
  whatYouNeed: string;
  needPassport: string;
  needId: string;
  needPhoto: string;
  needSpouseDocs: string;
  needLanguageTests: string;
  startForm: string;
  emailConsentPrefix: string;
  emailConsentSuffix: string;
  // Formulaire public (lien ouvert, sans dossier)
  publicConsentPrefix: string;
  publicConsentSuffix: string;
  publicWelcomeDefault: string;
  publicFormClosed: string;
  publicFormClosedDesc: string;
  publicSubmittedTitle: string;
  publicSubmittedDesc: string;
  publicFillAgain: string;
  publicTooMany: string;
  publicHumanCheck: string;
  noteTitle: string;
  noteHelp: string;
  notePlaceholder: string;
  yourFirm: string;
  needHelp: string;
  contactSupport: string;

  // Status screens
  loading: string;
  notFound: string;
  notFoundDesc: string;
  formUnavailable: string;
  formUnavailableDesc: string;
  submitting: string;
  submitError: string;
  submitErrorDesc: string;
  retry: string;
  formSubmitted: string;
  formSubmittedDesc: string;

  // Yes/No
  yes: string;
  no: string;

  // Inputs
  selectPlaceholder: string;
  specifyPlaceholder: string;

  // Validation errors
  fieldRequired: string;
  acceptRequired: string;
  enterStreet: string;
  enterCity: string;
  enterProvince: string;
  selectCountry: string;
  enterPostalCode: string;
  fillRequired: string;
  fixErrors: string;
  addTravel: string;
  completeTravel: string;
  selectOption: string;
  specifyAnswer: string;
  answerRequired: string;
  addEntry: string;
  fillField: (label: string) => string;
  frameYourFace: string;
  uploadRequired: string;
  confirmRequired: string;

  // Save & quit dialog
  saveQuitTitle: string;
  saveQuitDesc: string;
  saveQuitConfirm: string;
  saveQuitCancel: string;

  // Progress bar
  questionOf: (current: number, total: number) => string;
  lessThanMinute: string;
  aboutMinutes: (n: number) => string;
  progress: string;
  saving: string;
  saved: string;
  saveError: string;
  finalReview: string;

  // Components
  selectDate: string;
  deleteConfirm: string;
  entry: (n: number) => string;
  /** Nom affiché pour une personne d'une liste répétée dont on ne connaît pas encore le prénom. */
  repeatPerson: (n: number) => string;
  invalidCode: string;
  invalidNumber: string;
  reviewMode: string;
  missingQuestions: (current: number, total: number) => string;
  addFile: (current: number, max: number) => string;
  documentProvided: string;
  general: string;
  answerMissing: string;
  searchCountry: string;
  searchAddress: string;
  chooseOtherFile: string;
  add: string;
  whyItMatters: string;
  addTravelEntry: string;
  addAnotherTravelEntry: string;
  removeEntry: string;
  addChildEntry: string;
  addFamilyMemberEntry: string;
  addParentEntry: string;
  addSiblingEntry: string;
  addNationalityEntry: string;
  addEducationEntry: string;
  addSecondarySchoolEntry: string;
  addResidenceEntry: string;
  addLanguageEntry: string;
  addJobEntry: string;

  // Country picker / travel history
  selectCountryPlaceholder: string;
  noCountryFound: string;
  pinnedCountries: string;
  noTravelHistoryCheckbox: string;
  tripHeader: (index: number) => string;
  countryVisited: string;
  tripStartDateLabel: string;
  tripEndDateLabel: string;
  tripStartDateShort: string;
  tripEndDateShort: string;
  noTripsReview: string;
  tripDateRange: (start: string, end: string) => string;

  // Phone field labels
  phoneCountryLabel: string;
  phoneNumberLabel: string;

  // Inline helpers / OCR status
  examplePrefix: string;
  prefilledByScan: string;
  documentPreviewAlt: string;
  removeDocumentAria: string;
  documentUploaded: string;
  documentsUploaded: (count: number) => string;
  verifyingDocument: string;
  extractedInformation: string;
  extractedInfoHelp: string;
  fileFormatNotAccepted: string;
  fileTooLarge: (maxMB: number) => string;
  wrongDocumentDefault: string;

  // Entry aria-labels (MultiEntry / files)
  confirmDeleteEntryAria: (n: number) => string;
  removeEntryAria: (n: number) => string;
  confirmDeleteFileAria: (n: number) => string;
  removeFileAria: (n: number) => string;

  // Review screen
  reviewTitle: string;
  reviewSubtitle: string;
  unansweredQuestions: (count: number) => string;
  unansweredQuestionsHelp: string;
  finalConfirmation: string;
  submitAnswers: string;
  editFieldAria: (label: string) => string;
  notAnswered: string;
  documentNotAvailable: string;
  otherPrecision: (precision: string) => string;
  acceptedShort: string;

  // Feedback screen
  feedbackTitle: string;
  feedbackSubtitle: string;
  feedbackRatingLabel: string;
  feedbackTagsLabel: string;
  feedbackTagTooMany: string;
  feedbackTagConfusing: string;
  feedbackTagBugs: string;
  feedbackTagSlow: string;
  feedbackTagDocuments: string;
  feedbackCommentLabel: string;
  feedbackOptional: string;
  feedbackCommentPlaceholder: string;
  feedbackContinue: string;
  feedbackThanks: string;

  // Document upload
  takePhoto: string;
  useCamera: string;
  uploadFile: string;
  noDocument: string;
  uploadFileDesc: (maxSizeMB: number) => string;
  uploadImage: string;
  ocrRetryHint: string;
  ocrSkipHint: string;
  ocrRetryButton: string;
  ocrAttempt: (current: number, max: number) => string;
  ocrLastAttemptHint: string;
  requirementsNotMetDefault: string;
  ocrVerifying: string;
  fileAlreadyUploaded: string;
  uploadFailedTitle: string;
  uploadFailedBody: string;
  uploadTooLargeBody: string;
  ocrVerified: string;
  ocrFlagged: string;
  /** La lecture n'a pas pu se faire — panne de notre côté, rien à faire pour le client. */
  ocrUnverified: string;
  continueHintScanning: string;
  continueHintUploadFirst: string;
  continueHintAddTravel: string;
  dropFileHere: string;
  orDragAndDrop: string;

  // Address fields
  streetLabel: string;
  streetPlaceholder: string;
  aptLabel: string;
  aptPlaceholder: string;
  cityLabel: string;
  cityPlaceholder: string;
  provinceLabel: string;
  provincePlaceholder: string;
  countryLabel: string;
  postalCodeLabel: string;
  postalCodePlaceholder: string;
  manualAddressLink: string;
  editAddress: string;
}

const fr: UiTranslations = {
  back: "Retour",
  continue: "Continuer",
  save: "Enregistrer",
  review: "Révision",
  unableToAnswer: "Je ne suis pas en mesure de répondre",
  skipReasonTitle: "Pourquoi ne pouvez-vous pas répondre ?",
  skipDocReasonTitle: "Pourquoi ne pouvez-vous pas fournir ce document ?",
  skipReasonDescription: "Expliquez brièvement pourquoi. Cela aide votre conseiller à savoir que le blanc est voulu.",
  skipReasonPlaceholder: "Ex. : cette question ne s'applique pas à ma situation…",
  skipReasonConfirm: "Confirmer",
  cancel: "Annuler",
  saveAndQuit: "Sauvegarder et quitter",
  continueForm: "Continuer le formulaire",
  poweredBy: "Powered by",

  changeLanguageAria: (current) => `Changer de langue (actuellement : ${current})`,

  hello: "Bonjour",
  defaultClientName: "Client",
  firmLogoAlt: "Logo du cabinet",
  welcomeMessage: (firm) =>
    `Vous ouvrez un formulaire de ${firm}. Un processus simple, étape par étape, pour soumettre votre dossier complet.`,
  autosaveReassurance:
    "Vos réponses sont enregistrées automatiquement. Vous pouvez fermer cette page et revenir plus tard avec le même lien.",
  whatYouNeed: "Ce dont vous aurez besoin :",
  needPassport: "Votre passeport valide",
  needId: "Pièce d'identité nationale (si applicable)",
  needPhoto: "Photo d'identité récente (moins de 3 mois)",
  needSpouseDocs: "Documents du conjoint(e) si applicable",
  needLanguageTests: "Résultats de tests linguistiques (si applicable)",
  emailConsentPrefix: "J'accepte de recevoir des rappels par courriel de ",
  emailConsentSuffix: " jusqu'à la soumission de mon formulaire.",
  publicConsentPrefix: "J'accepte que ",
  publicConsentSuffix: " me contacte au sujet de ma demande.",
  publicWelcomeDefault: "Répondez à quelques questions : vos réponses nous permettent de vous orienter vers la bonne démarche.",
  publicFormClosed: "Formulaire fermé",
  publicFormClosedDesc: "Ce formulaire n'accepte plus de réponses. Contactez directement le cabinet.",
  publicSubmittedTitle: "Merci, nous avons bien reçu vos réponses.",
  publicSubmittedDesc: "Le cabinet vous recontactera prochainement.",
  publicFillAgain: "Remplir une nouvelle demande",
  publicTooMany: "Vous avez déjà envoyé plusieurs demandes depuis cet appareil. Réessayez plus tard, ou écrivez directement au cabinet.",
  publicHumanCheck: "Merci de confirmer que vous n'êtes pas un robot, puis réessayez.",
  noteTitle: "Quelque chose à ajouter ?",
  noteHelp: "Facultatif. Ce que vous écrivez ici sera lu avec vos réponses.",
  notePlaceholder: "Une précision sur votre situation, une question, un détail qui n'entrait dans aucune case…",
  yourFirm: "votre cabinet",
  startForm: "Commencer",
  needHelp: "Besoin d'aide ?",
  contactSupport: "Contactez le support",

  loading: "Chargement de votre dossier...",
  notFound: "Dossier introuvable",
  notFoundDesc:
    "Nous n'avons pas pu trouver votre dossier. Veuillez vérifier le lien ou contacter votre cabinet.",
  formUnavailable: "Formulaire non disponible",
  formUnavailableDesc:
    "Votre type de demande n'a pas encore été assigné. Veuillez contacter votre cabinet pour continuer.",
  submitting: "Envoi de vos réponses en cours...",
  submitError: "Erreur lors de l'envoi",
  submitErrorDesc:
    "Une erreur est survenue lors de l'envoi de vos réponses. Veuillez réessayer.",
  retry: "Réessayer",
  formSubmitted: "Formulaire envoyé",
  formSubmittedDesc:
    "Vos réponses ont été soumises avec succès. Votre cabinet vous contactera prochainement.",

  yes: "Oui",
  no: "Non",

  selectPlaceholder: "Sélectionnez...",
  specifyPlaceholder: "Veuillez préciser...",

  fieldRequired: "Veuillez répondre à cette question",
  acceptRequired: "Veuillez accepter pour continuer",
  enterStreet: "Veuillez entrer l'adresse civique",
  enterCity: "Veuillez entrer la ville",
  enterProvince: "Veuillez entrer la province",
  selectCountry: "Veuillez sélectionner le pays",
  enterPostalCode: "Veuillez entrer le code postal",
  fillRequired: "Veuillez remplir tous les champs obligatoires",
  fixErrors: "Veuillez corriger les champs en erreur",
  addTravel: 'Veuillez ajouter au moins un voyage ou cocher "Aucun voyage"',
  completeTravel:
    "Veuillez compléter toutes les informations de chaque voyage",
  selectOption: "Veuillez sélectionner une option",
  specifyAnswer: "Veuillez préciser votre réponse",
  answerRequired: "Veuillez répondre à cette question",
  addEntry: "Veuillez ajouter au moins une entrée",
  fillField: (label) => `Veuillez remplir le champ "${label}" pour chaque entrée`,
  frameYourFace: "Cadrez votre visage comme ceci",
  uploadRequired: "Veuillez télécharger le document requis",
  confirmRequired: "Veuillez confirmer avant de continuer",

  saveQuitTitle: "Sauvegarder et quitter",
  saveQuitDesc:
    "Vos réponses sont sauvegardées automatiquement. Vous pourrez reprendre le formulaire plus tard en utilisant le même lien.",
  saveQuitConfirm: "Sauvegarder et quitter",
  saveQuitCancel: "Continuer le formulaire",

  questionOf: (c, t) => `Question ${c} sur ${t}`,
  lessThanMinute: "moins d'une minute",
  aboutMinutes: (n) => `environ ${n} minutes`,
  progress: "Progression",
  saving: "Sauvegarde...",
  saved: "Sauvegardé",
  saveError: "Erreur de sauvegarde",
  finalReview: "Révision finale",

  selectDate: "Sélectionner une date",
  deleteConfirm: "Supprimer ?",
  entry: (n) => `Entrée ${n}`,
  repeatPerson: (n) => `Personne ${n}`,
  invalidCode: "Indicatif invalide (ex : +1, +33)",
  invalidNumber: "Numéro invalide",
  reviewMode: "Mode révision",
  missingQuestions: (c, t) => `Question ${c} sur ${t} manquante${t > 1 ? "s" : ""}`,
  addFile: (c, m) => `Ajouter un fichier (${c}/${m})`,
  documentProvided: "Document fourni",
  general: "Général",
  answerMissing: "Répondre aux questions manquantes",
  searchCountry: "Rechercher un pays...",
  searchAddress: "Rechercher une adresse...",
  chooseOtherFile: "Choisir un autre fichier",
  add: "+ Ajouter",
  whyItMatters: "Pourquoi c'est important",
  addTravelEntry: "Ajouter un voyage",
  addAnotherTravelEntry: "Ajouter un autre voyage",
  removeEntry: "Supprimer",
  addChildEntry: "+ Ajouter un enfant",
  addFamilyMemberEntry: "+ Ajouter un membre de la famille",
  addParentEntry: "+ Ajouter un parent",
  addSiblingEntry: "+ Ajouter un frère/une sœur",
  addNationalityEntry: "+ Ajouter une nationalité",
  addEducationEntry: "+ Ajouter un niveau d'études",
  addSecondarySchoolEntry: "+ Ajouter une école secondaire",
  addResidenceEntry: "+ Ajouter un pays de résidence",
  addLanguageEntry: "+ Ajouter une langue",
  addJobEntry: "+ Ajouter un emploi",

  selectCountryPlaceholder: "Sélectionnez un pays",
  noCountryFound: "Aucun pays trouvé",
  pinnedCountries: "Fréquents",
  noTravelHistoryCheckbox: "Je n'ai pas voyagé à l'étranger ces 5 dernières années",
  tripHeader: (i) => `Voyage ${i}`,
  countryVisited: "Pays visité",
  tripStartDateLabel: "Date de début",
  tripEndDateLabel: "Date de fin",
  tripStartDateShort: "Début",
  tripEndDateShort: "Fin",
  noTripsReview: "Aucun voyage",
  tripDateRange: (start, end) => `du ${start} au ${end}`,

  phoneCountryLabel: "Pays *",
  phoneNumberLabel: "Numéro *",

  examplePrefix: "Exemple : ",
  prefilledByScan: "Pré-rempli par scan du document",
  documentPreviewAlt: "Aperçu du document",
  removeDocumentAria: "Supprimer le document",
  documentUploaded: "Document téléchargé",
  documentsUploaded: (c) => `${c} document${c > 1 ? "s" : ""} téléchargé${c > 1 ? "s" : ""}`,
  verifyingDocument: "Vérification du document...",
  extractedInformation: "Informations extraites",
  extractedInfoHelp: "Vérifiez et corrigez si nécessaire. Ces questions ne seront pas reposées.",
  fileFormatNotAccepted: "Format de fichier non accepté. Utilisez JPG, PNG ou PDF.",
  fileTooLarge: (mb) => `Le fichier est trop volumineux. Maximum ${mb} MB.`,
  wrongDocumentDefault: "Le document téléchargé ne correspond pas à ce qui est demandé.",

  confirmDeleteEntryAria: (n) => `Confirmer la suppression de l'entrée ${n}`,
  removeEntryAria: (n) => `Supprimer l'entrée ${n}`,
  confirmDeleteFileAria: (n) => `Confirmer la suppression du fichier ${n}`,
  removeFileAria: (n) => `Supprimer le fichier ${n}`,

  reviewTitle: "Vérifiez vos informations",
  reviewSubtitle: "Assurez-vous que tout est correct avant d'envoyer.",
  unansweredQuestions: (c) => `${c} question${c > 1 ? "s" : ""} sans réponse`,
  unansweredQuestionsHelp: "Vous devez répondre à toutes les questions avant d'envoyer le formulaire.",
  finalConfirmation: "Je confirme que toutes les informations ci-dessus sont exactes et que les documents téléchargés sont authentiques.",
  submitAnswers: "Envoyer les réponses",
  editFieldAria: (label) => `Modifier : ${label}`,
  notAnswered: "Non répondu",
  documentNotAvailable: "Document non disponible",
  otherPrecision: (p) => `Autre — ${p}`,
  acceptedShort: "✓ Accepté",

  feedbackTitle: "Votre avis nous aide",
  feedbackSubtitle: "Deux questions rapides sur votre expérience — entièrement optionnelles.",
  feedbackRatingLabel: "Comment évalueriez-vous votre expérience globale ?",
  feedbackTagsLabel: "Qu'est-ce qui pourrait être amélioré ?",
  feedbackTagTooMany: "Trop de questions",
  feedbackTagConfusing: "Questions pas claires",
  feedbackTagBugs: "Bugs ou erreurs",
  feedbackTagSlow: "Trop lent",
  feedbackTagDocuments: "Problèmes avec les documents",
  feedbackCommentLabel: "Des commentaires ou suggestions ?",
  feedbackOptional: "optionnel",
  feedbackCommentPlaceholder: "Écrivez vos commentaires ici...",
  feedbackContinue: "Envoyer mon avis",
  feedbackThanks: "Merci pour votre avis !",

  takePhoto: "Prendre une photo",
  useCamera: "Utilisez votre appareil photo",
  uploadFile: "Télécharger un fichier",
  uploadFileDesc: (maxSizeMB) => `JPG, PNG ou PDF (max ${maxSizeMB} MB)`,
  noDocument: "Je ne possède pas ce document",
  uploadImage: "Télécharger une image",
  ocrRetryHint: "On n'arrive pas à lire le document. Essayez de prendre la photo dans un endroit bien éclairé, sans reflets, avec le document à plat et bien centré.",
  ocrSkipHint: "On n'arrive pas à lire le document, mais ce n'est pas grave. Vous pouvez continuer.",
  ocrRetryButton: "Reprendre la photo",
  ocrAttempt: (c, m) => `Essai ${c} sur ${m}`,
  ocrLastAttemptHint: "Dernier essai. Ensuite, la vérification s'arrête et vous pourrez continuer, même si le document n'est pas confirmé.",
  requirementsNotMetDefault: "Le document ne remplit pas toutes les conditions demandées.",
  ocrVerifying: "Vérification…",
  fileAlreadyUploaded: "Fichier déjà téléversé",
  uploadFailedTitle: "Le document n'a pas été envoyé",
  uploadFailedBody: "La connexion a été interrompue pendant l'envoi. Vérifiez votre réseau, puis réessayez : le fichier est encore ici.",
  uploadTooLargeBody: "Le fichier est trop volumineux pour être envoyé. Prenez une photo plus petite ou compressez le PDF, puis réessayez.",
  ocrVerified: "Vérifié",
  ocrFlagged: "À revoir",
  ocrUnverified: "Reçu, non vérifié",
  continueHintScanning: "On vérifie votre document, un instant…",
  continueHintUploadFirst: "Ajoutez un document avant de continuer",
  continueHintAddTravel: "Ajoutez un voyage ou cochez « Aucun voyage »",
  dropFileHere: "Déposez votre fichier ici",
  orDragAndDrop: "ou glissez-déposez votre fichier ici",

  streetLabel: "Adresse civique *",
  streetPlaceholder: "123 Rue Principale",
  aptLabel: "Appartement / Unité (optionnel)",
  aptPlaceholder: "App. 4B",
  cityLabel: "Ville *",
  cityPlaceholder: "Montréal",
  provinceLabel: "Province / État *",
  provincePlaceholder: "Québec",
  countryLabel: "Pays *",
  postalCodeLabel: "Code postal *",
  postalCodePlaceholder: "H2X 1A1",
  manualAddressLink: "Vous ne trouvez pas votre adresse ? Entrez-la manuellement",
  editAddress: "Modifier",
};

const en: UiTranslations = {
  back: "Back",
  continue: "Continue",
  save: "Save",
  review: "Review",
  unableToAnswer: "I am unable to answer",
  skipReasonTitle: "Why can't you answer?",
  skipDocReasonTitle: "Why can't you provide this document?",
  skipReasonDescription: "Briefly explain why. It helps your advisor know the blank is intentional.",
  skipReasonPlaceholder: "E.g. this question doesn't apply to my situation…",
  skipReasonConfirm: "Confirm",
  cancel: "Cancel",
  saveAndQuit: "Save and quit",
  continueForm: "Continue form",
  poweredBy: "Powered by",

  changeLanguageAria: (current) => `Change language (currently: ${current})`,

  hello: "Hello",
  defaultClientName: "Client",
  firmLogoAlt: "Firm logo",
  welcomeMessage: (firm) =>
    `You are opening a form from ${firm}. A simple, step-by-step process to submit your complete file.`,
  autosaveReassurance:
    "Your answers save automatically. You can close this page and come back later with the same link.",
  whatYouNeed: "What you will need:",
  needPassport: "Your valid passport",
  needId: "National ID card (if applicable)",
  needPhoto: "Recent ID photo (less than 3 months old)",
  needSpouseDocs: "Spouse documents if applicable",
  needLanguageTests: "Language test results (if applicable)",
  emailConsentPrefix: "I agree to receive email reminders from ",
  emailConsentSuffix: " until I submit my form.",
  publicConsentPrefix: "I agree that ",
  publicConsentSuffix: " may contact me about my request.",
  publicWelcomeDefault: "Answer a few questions: your answers help us point you to the right next step.",
  publicFormClosed: "Form closed",
  publicFormClosedDesc: "This form no longer accepts answers. Please contact the firm directly.",
  publicSubmittedTitle: "Thank you, we have received your answers.",
  publicSubmittedDesc: "The firm will get back to you shortly.",
  publicFillAgain: "Fill out another request",
  publicTooMany: "You have already sent several requests from this device. Please try again later, or contact the firm directly.",
  publicHumanCheck: "Please confirm you are not a robot, then try again.",
  noteTitle: "Anything to add?",
  noteHelp: "Optional. What you write here will be read along with your answers.",
  notePlaceholder: "Something about your situation, a question, a detail that fit in no box…",
  yourFirm: "your firm",
  startForm: "Start",
  needHelp: "Need help?",
  contactSupport: "Contact support",

  loading: "Loading your file...",
  notFound: "File not found",
  notFoundDesc:
    "We could not find your file. Please check the link or contact your firm.",
  formUnavailable: "Form unavailable",
  formUnavailableDesc:
    "Your application type has not been assigned yet. Please contact your firm to continue.",
  submitting: "Submitting your answers...",
  submitError: "Submission error",
  submitErrorDesc:
    "An error occurred while submitting your answers. Please try again.",
  retry: "Try again",
  formSubmitted: "Form submitted",
  formSubmittedDesc:
    "Your answers have been submitted successfully. Your firm will contact you shortly.",

  yes: "Yes",
  no: "No",

  selectPlaceholder: "Select...",
  specifyPlaceholder: "Please specify...",

  fieldRequired: "Please answer this question",
  acceptRequired: "Please accept to continue",
  enterStreet: "Please enter the street address",
  enterCity: "Please enter the city",
  enterProvince: "Please enter the province",
  selectCountry: "Please select the country",
  enterPostalCode: "Please enter the postal code",
  fillRequired: "Please fill in all required fields",
  fixErrors: "Please fix the errors",
  addTravel: 'Please add at least one trip or check "No travel"',
  completeTravel: "Please complete all travel information",
  selectOption: "Please select an option",
  specifyAnswer: "Please specify your answer",
  answerRequired: "Please answer this question",
  addEntry: "Please add at least one entry",
  fillField: (label) => `Please fill in the "${label}" field for each entry`,
  frameYourFace: "Frame your face like this",
  uploadRequired: "Please upload the required document",
  confirmRequired: "Please confirm before continuing",

  saveQuitTitle: "Save and quit",
  saveQuitDesc:
    "Your answers are saved automatically. You can resume the form later using the same link.",
  saveQuitConfirm: "Save and quit",
  saveQuitCancel: "Continue form",

  questionOf: (c, t) => `Question ${c} of ${t}`,
  lessThanMinute: "less than a minute",
  aboutMinutes: (n) => `about ${n} minutes`,
  progress: "Progress",
  saving: "Saving...",
  saved: "Saved",
  saveError: "Save error",
  finalReview: "Final review",

  selectDate: "Select a date",
  deleteConfirm: "Delete?",
  entry: (n) => `Entry ${n}`,
  repeatPerson: (n) => `Person ${n}`,
  invalidCode: "Invalid code (e.g. +1, +33)",
  invalidNumber: "Invalid number",
  reviewMode: "Review mode",
  missingQuestions: (c, t) => `Question ${c} of ${t} missing`,
  addFile: (c, m) => `Add a file (${c}/${m})`,
  documentProvided: "Document provided",
  general: "General",
  answerMissing: "Answer missing questions",
  searchCountry: "Search a country...",
  searchAddress: "Search an address...",
  chooseOtherFile: "Choose another file",
  add: "+ Add",
  whyItMatters: "Why this matters",
  addTravelEntry: "Add a trip",
  addAnotherTravelEntry: "Add another trip",
  removeEntry: "Remove",
  addChildEntry: "+ Add a child",
  addFamilyMemberEntry: "+ Add a family member",
  addParentEntry: "+ Add a parent",
  addSiblingEntry: "+ Add a sibling",
  addNationalityEntry: "+ Add a nationality",
  addEducationEntry: "+ Add an education level",
  addSecondarySchoolEntry: "+ Add a secondary school",
  addResidenceEntry: "+ Add a country of residence",
  addLanguageEntry: "+ Add a language",
  addJobEntry: "+ Add a job",

  selectCountryPlaceholder: "Select a country",
  noCountryFound: "No country found",
  pinnedCountries: "Frequent",
  noTravelHistoryCheckbox: "I have not travelled abroad in the past 5 years",
  tripHeader: (i) => `Trip ${i}`,
  countryVisited: "Country visited",
  tripStartDateLabel: "Start date",
  tripEndDateLabel: "End date",
  tripStartDateShort: "Start",
  tripEndDateShort: "End",
  noTripsReview: "No trips",
  tripDateRange: (start, end) => `from ${start} to ${end}`,

  phoneCountryLabel: "Country *",
  phoneNumberLabel: "Number *",

  examplePrefix: "Example: ",
  prefilledByScan: "Pre-filled from document scan",
  documentPreviewAlt: "Document preview",
  removeDocumentAria: "Remove document",
  documentUploaded: "Document uploaded",
  documentsUploaded: (c) => `${c} document${c > 1 ? "s" : ""} uploaded`,
  verifyingDocument: "Verifying document...",
  extractedInformation: "Extracted information",
  extractedInfoHelp: "Review and correct if needed. These questions will not be asked again.",
  fileFormatNotAccepted: "File format not accepted. Use JPG, PNG or PDF.",
  fileTooLarge: (mb) => `File is too large. Maximum ${mb} MB.`,
  wrongDocumentDefault: "The uploaded document does not match what was requested.",

  confirmDeleteEntryAria: (n) => `Confirm deletion of entry ${n}`,
  removeEntryAria: (n) => `Remove entry ${n}`,
  confirmDeleteFileAria: (n) => `Confirm deletion of file ${n}`,
  removeFileAria: (n) => `Remove file ${n}`,

  reviewTitle: "Review your information",
  reviewSubtitle: "Make sure everything is correct before submitting.",
  unansweredQuestions: (c) => `${c} unanswered question${c > 1 ? "s" : ""}`,
  unansweredQuestionsHelp: "You must answer all questions before submitting the form.",
  finalConfirmation: "I confirm that all the information above is accurate and that the uploaded documents are authentic.",
  submitAnswers: "Submit answers",
  editFieldAria: (label) => `Edit: ${label}`,
  notAnswered: "Not answered",
  documentNotAvailable: "Document not available",
  otherPrecision: (p) => `Other — ${p}`,
  acceptedShort: "✓ Accepted",

  feedbackTitle: "Your feedback helps us",
  feedbackSubtitle: "Two quick questions about your experience — completely optional.",
  feedbackRatingLabel: "How would you rate your overall experience?",
  feedbackTagsLabel: "What could be improved?",
  feedbackTagTooMany: "Too many questions",
  feedbackTagConfusing: "Unclear questions",
  feedbackTagBugs: "Bugs or errors",
  feedbackTagSlow: "Too slow",
  feedbackTagDocuments: "Issues with documents",
  feedbackCommentLabel: "Any comments or suggestions?",
  feedbackOptional: "optional",
  feedbackCommentPlaceholder: "Write your comments here...",
  feedbackContinue: "Send my feedback",
  feedbackThanks: "Thank you for your feedback!",

  takePhoto: "Take a photo",
  useCamera: "Use your camera",
  uploadFile: "Upload a file",
  uploadFileDesc: (maxSizeMB) => `JPG, PNG or PDF (max ${maxSizeMB} MB)`,
  noDocument: "I don't have this document",
  uploadImage: "Upload an image",
  ocrRetryHint: "We can't read the document. Try taking the photo in good light, without glare, with the document flat and centered in the frame.",
  ocrSkipHint: "We can't read the document, but that's okay. You can keep going.",
  ocrRetryButton: "Take another photo",
  ocrAttempt: (c, m) => `Attempt ${c} of ${m}`,
  ocrLastAttemptHint: "Last attempt. After this, verification stops and you can continue, even if the document isn't confirmed.",
  requirementsNotMetDefault: "The document does not meet all the required conditions.",
  ocrVerifying: "Verifying…",
  fileAlreadyUploaded: "File already uploaded",
  uploadFailedTitle: "The document was not sent",
  uploadFailedBody: "The connection dropped while sending. Check your network, then try again: the file is still here.",
  uploadTooLargeBody: "The file is too large to send. Take a smaller photo or compress the PDF, then try again.",
  ocrVerified: "Verified",
  ocrFlagged: "To review",
  ocrUnverified: "Received, not checked",
  continueHintScanning: "Checking your document, one moment…",
  continueHintUploadFirst: "Add a document before continuing",
  continueHintAddTravel: "Add a trip or check \"No travel\"",
  dropFileHere: "Drop your file here",
  orDragAndDrop: "or drag and drop your file here",

  streetLabel: "Street address *",
  streetPlaceholder: "123 Main Street",
  aptLabel: "Apartment / Unit (optional)",
  aptPlaceholder: "Apt. 4B",
  cityLabel: "City *",
  cityPlaceholder: "Montreal",
  provinceLabel: "Province / State *",
  provincePlaceholder: "Quebec",
  countryLabel: "Country *",
  postalCodeLabel: "Postal code *",
  postalCodePlaceholder: "H2X 1A1",
  manualAddressLink: "Can't find your address? Enter it manually",
  editAddress: "Edit",
};

const es: UiTranslations = {
  back: "Volver",
  continue: "Continuar",
  save: "Guardar",
  review: "Revisión",
  unableToAnswer: "No puedo responder",
  skipReasonTitle: "¿Por qué no puede responder?",
  skipDocReasonTitle: "¿Por qué no puede proporcionar este documento?",
  skipReasonDescription: "Explique brevemente por qué. Ayuda a su asesor a saber que el espacio en blanco es intencional.",
  skipReasonPlaceholder: "Ej.: esta pregunta no se aplica a mi situación…",
  skipReasonConfirm: "Confirmar",
  cancel: "Cancelar",
  saveAndQuit: "Guardar y salir",
  continueForm: "Continuar formulario",
  poweredBy: "Powered by",

  changeLanguageAria: (current) => `Cambiar idioma (actualmente: ${current})`,

  hello: "Hola",
  defaultClientName: "Cliente",
  firmLogoAlt: "Logotipo del despacho",
  welcomeMessage: (firm) =>
    `Está abriendo un formulario de ${firm}. Un proceso simple, paso a paso, para enviar su expediente completo.`,
  autosaveReassurance:
    "Sus respuestas se guardan automáticamente. Puede cerrar esta página y volver más tarde con el mismo enlace.",
  whatYouNeed: "Lo que necesitará:",
  needPassport: "Su pasaporte válido",
  needId: "Documento de identidad nacional (si corresponde)",
  needPhoto: "Foto de identidad reciente (menos de 3 meses)",
  needSpouseDocs: "Documentos del cónyuge si corresponde",
  needLanguageTests: "Resultados de pruebas lingüísticas (si corresponde)",
  emailConsentPrefix: "Acepto recibir recordatorios por correo electrónico de ",
  emailConsentSuffix: " hasta el envío de mi formulario.",
  publicConsentPrefix: "Acepto que ",
  publicConsentSuffix: " me contacte sobre mi solicitud.",
  publicWelcomeDefault: "Responda a algunas preguntas: sus respuestas nos permiten orientarle hacia el trámite adecuado.",
  publicFormClosed: "Formulario cerrado",
  publicFormClosedDesc: "Este formulario ya no acepta respuestas. Contacte directamente al despacho.",
  publicSubmittedTitle: "Gracias, hemos recibido sus respuestas.",
  publicSubmittedDesc: "El despacho se pondrá en contacto con usted en breve.",
  publicFillAgain: "Enviar otra solicitud",
  publicTooMany: "Ya ha enviado varias solicitudes desde este dispositivo. Vuelva a intentarlo más tarde o escriba directamente al despacho.",
  publicHumanCheck: "Confirme que no es un robot e inténtelo de nuevo.",
  noteTitle: "¿Algo que añadir?",
  noteHelp: "Opcional. Lo que escriba aquí se leerá junto con sus respuestas.",
  notePlaceholder: "Una precisión sobre su situación, una pregunta, un detalle que no cabía en ninguna casilla…",
  yourFirm: "su despacho",
  startForm: "Comenzar",
  needHelp: "¿Necesita ayuda?",
  contactSupport: "Contacte al soporte",

  loading: "Cargando su expediente...",
  notFound: "Expediente no encontrado",
  notFoundDesc:
    "No pudimos encontrar su expediente. Verifique el enlace o contacte a su firma.",
  formUnavailable: "Formulario no disponible",
  formUnavailableDesc:
    "Su tipo de solicitud aún no ha sido asignado. Contacte a su firma para continuar.",
  submitting: "Enviando sus respuestas...",
  submitError: "Error al enviar",
  submitErrorDesc:
    "Ocurrió un error al enviar sus respuestas. Intente nuevamente.",
  retry: "Reintentar",
  formSubmitted: "Formulario enviado",
  formSubmittedDesc:
    "Sus respuestas han sido enviadas con éxito. Su firma se comunicará con usted pronto.",

  yes: "Sí",
  no: "No",

  selectPlaceholder: "Seleccione...",
  specifyPlaceholder: "Por favor especifique...",

  fieldRequired: "Por favor responda esta pregunta",
  acceptRequired: "Por favor acepte para continuar",
  enterStreet: "Por favor ingrese la dirección",
  enterCity: "Por favor ingrese la ciudad",
  enterProvince: "Por favor ingrese la provincia",
  selectCountry: "Por favor seleccione el país",
  enterPostalCode: "Por favor ingrese el código postal",
  fillRequired: "Por favor complete todos los campos obligatorios",
  fixErrors: "Por favor corrija los errores",
  addTravel: 'Por favor agregue al menos un viaje o marque "Sin viajes"',
  completeTravel: "Por favor complete toda la información de cada viaje",
  selectOption: "Por favor seleccione una opción",
  specifyAnswer: "Por favor especifique su respuesta",
  answerRequired: "Por favor responda esta pregunta",
  addEntry: "Por favor agregue al menos una entrada",
  fillField: (label) =>
    `Por favor complete el campo "${label}" para cada entrada`,
  frameYourFace: "Encuadre su rostro así",
  uploadRequired: "Por favor cargue el documento requerido",
  confirmRequired: "Por favor confirme antes de continuar",

  saveQuitTitle: "Guardar y salir",
  saveQuitDesc:
    "Sus respuestas se guardan automáticamente. Puede retomar el formulario más tarde usando el mismo enlace.",
  saveQuitConfirm: "Guardar y salir",
  saveQuitCancel: "Continuar formulario",

  questionOf: (c, t) => `Pregunta ${c} de ${t}`,
  lessThanMinute: "menos de un minuto",
  aboutMinutes: (n) => `aproximadamente ${n} minutos`,
  progress: "Progreso",
  saving: "Guardando...",
  saved: "Guardado",
  saveError: "Error al guardar",
  finalReview: "Revisión final",

  selectDate: "Seleccionar una fecha",
  deleteConfirm: "¿Eliminar?",
  entry: (n) => `Entrada ${n}`,
  repeatPerson: (n) => `Persona ${n}`,
  invalidCode: "Código inválido (ej.: +1, +33)",
  invalidNumber: "Número inválido",
  reviewMode: "Modo revisión",
  missingQuestions: (c, t) => `Pregunta ${c} de ${t} faltante${t > 1 ? "s" : ""}`,
  addFile: (c, m) => `Agregar un archivo (${c}/${m})`,
  documentProvided: "Documento proporcionado",
  general: "General",
  answerMissing: "Responder preguntas faltantes",
  searchCountry: "Buscar un país...",
  searchAddress: "Buscar una dirección...",
  chooseOtherFile: "Elegir otro archivo",
  add: "+ Agregar",
  whyItMatters: "Por qué es importante",
  addTravelEntry: "Añadir un viaje",
  addAnotherTravelEntry: "Añadir otro viaje",
  removeEntry: "Eliminar",
  addChildEntry: "+ Añadir un hijo",
  addFamilyMemberEntry: "+ Añadir un familiar",
  addParentEntry: "+ Añadir un padre/madre",
  addSiblingEntry: "+ Añadir un hermano/a",
  addNationalityEntry: "+ Añadir una nacionalidad",
  addEducationEntry: "+ Añadir un nivel de estudios",
  addSecondarySchoolEntry: "+ Añadir una escuela secundaria",
  addResidenceEntry: "+ Añadir un país de residencia",
  addLanguageEntry: "+ Añadir un idioma",
  addJobEntry: "+ Añadir un empleo",

  selectCountryPlaceholder: "Seleccione un país",
  noCountryFound: "No se encontró ningún país",
  pinnedCountries: "Frecuentes",
  noTravelHistoryCheckbox: "No he viajado al extranjero en los últimos 5 años",
  tripHeader: (i) => `Viaje ${i}`,
  countryVisited: "País visitado",
  tripStartDateLabel: "Fecha de inicio",
  tripEndDateLabel: "Fecha de fin",
  tripStartDateShort: "Inicio",
  tripEndDateShort: "Fin",
  noTripsReview: "Ningún viaje",
  tripDateRange: (start, end) => `del ${start} al ${end}`,

  phoneCountryLabel: "País *",
  phoneNumberLabel: "Número *",

  examplePrefix: "Ejemplo: ",
  prefilledByScan: "Rellenado previamente por escaneo del documento",
  documentPreviewAlt: "Vista previa del documento",
  removeDocumentAria: "Eliminar el documento",
  documentUploaded: "Documento cargado",
  documentsUploaded: (c) => `${c} documento${c > 1 ? "s" : ""} cargado${c > 1 ? "s" : ""}`,
  verifyingDocument: "Verificando el documento...",
  extractedInformation: "Información extraída",
  extractedInfoHelp: "Verifique y corrija si es necesario. Estas preguntas no se volverán a hacer.",
  fileFormatNotAccepted: "Formato de archivo no aceptado. Use JPG, PNG o PDF.",
  fileTooLarge: (mb) => `El archivo es demasiado grande. Máximo ${mb} MB.`,
  wrongDocumentDefault: "El documento cargado no corresponde con lo solicitado.",

  confirmDeleteEntryAria: (n) => `Confirmar la eliminación de la entrada ${n}`,
  removeEntryAria: (n) => `Eliminar la entrada ${n}`,
  confirmDeleteFileAria: (n) => `Confirmar la eliminación del archivo ${n}`,
  removeFileAria: (n) => `Eliminar el archivo ${n}`,

  reviewTitle: "Revise su información",
  reviewSubtitle: "Asegúrese de que todo sea correcto antes de enviar.",
  unansweredQuestions: (c) => `${c} pregunta${c > 1 ? "s" : ""} sin responder`,
  unansweredQuestionsHelp: "Debe responder todas las preguntas antes de enviar el formulario.",
  finalConfirmation: "Confirmo que toda la información anterior es exacta y que los documentos cargados son auténticos.",
  submitAnswers: "Enviar respuestas",
  editFieldAria: (label) => `Editar: ${label}`,
  notAnswered: "Sin responder",
  documentNotAvailable: "Documento no disponible",
  otherPrecision: (p) => `Otro — ${p}`,
  acceptedShort: "✓ Aceptado",

  feedbackTitle: "Su opinión nos ayuda",
  feedbackSubtitle: "Dos preguntas rápidas sobre su experiencia — completamente opcionales.",
  feedbackRatingLabel: "¿Cómo calificaría su experiencia general?",
  feedbackTagsLabel: "¿Qué podría mejorarse?",
  feedbackTagTooMany: "Demasiadas preguntas",
  feedbackTagConfusing: "Preguntas poco claras",
  feedbackTagBugs: "Errores o bugs",
  feedbackTagSlow: "Demasiado lento",
  feedbackTagDocuments: "Problemas con los documentos",
  feedbackCommentLabel: "¿Comentarios o sugerencias?",
  feedbackOptional: "opcional",
  feedbackCommentPlaceholder: "Escriba sus comentarios aquí...",
  feedbackContinue: "Enviar mi opinión",
  feedbackThanks: "¡Gracias por su opinión!",

  takePhoto: "Tomar una foto",
  useCamera: "Use su cámara",
  uploadFile: "Subir un archivo",
  uploadFileDesc: (maxSizeMB) => `JPG, PNG o PDF (máx. ${maxSizeMB} MB)`,
  noDocument: "No tengo este documento",
  uploadImage: "Subir una imagen",
  ocrRetryHint: "No podemos leer el documento. Intente tomar la foto con buena iluminación, sin reflejos, con el documento plano y centrado.",
  ocrSkipHint: "No podemos leer el documento, pero no se preocupe. Puede continuar.",
  ocrRetryButton: "Tomar otra foto",
  ocrAttempt: (c, m) => `Intento ${c} de ${m}`,
  ocrLastAttemptHint: "Último intento. Después, la verificación se detiene y podrá continuar, aunque el documento no esté confirmado.",
  requirementsNotMetDefault: "El documento no cumple con todas las condiciones requeridas.",
  ocrVerifying: "Verificando…",
  fileAlreadyUploaded: "Archivo ya cargado",
  uploadFailedTitle: "El documento no se envió",
  uploadFailedBody: "La conexión se interrumpió durante el envío. Revise su red y vuelva a intentarlo: el archivo sigue aquí.",
  uploadTooLargeBody: "El archivo es demasiado grande para enviarlo. Tome una foto más pequeña o comprima el PDF y vuelva a intentarlo.",
  ocrVerified: "Verificado",
  ocrFlagged: "Por revisar",
  ocrUnverified: "Recibido, sin verificar",
  continueHintScanning: "Estamos verificando su documento, un momento…",
  continueHintUploadFirst: "Agregue un documento antes de continuar",
  continueHintAddTravel: "Agregue un viaje o marque \"Sin viajes\"",
  dropFileHere: "Suelte su archivo aquí",
  orDragAndDrop: "o arrastre y suelte su archivo aquí",

  streetLabel: "Dirección *",
  streetPlaceholder: "123 Calle Principal",
  aptLabel: "Apartamento / Unidad (opcional)",
  aptPlaceholder: "Apt. 4B",
  cityLabel: "Ciudad *",
  cityPlaceholder: "Montreal",
  provinceLabel: "Provincia / Estado *",
  provincePlaceholder: "Quebec",
  countryLabel: "País *",
  postalCodeLabel: "Código postal *",
  postalCodePlaceholder: "H2X 1A1",
  manualAddressLink: "¿No encuentra su dirección? Ingrésela manualmente",
  editAddress: "Editar",
};

const ALL_TRANSLATIONS: Record<string, UiTranslations> = { fr, en, es };

export const SUPPORTED_LANGUAGES = ['fr', 'en', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
};

// Detect the user's preferred language from the browser. Falls back to 'fr'.
// Checks navigator.languages first (an ordered list of preferences), then navigator.language.
export function detectBrowserLanguage(): SupportedLanguage {
  if (typeof navigator === 'undefined') return 'fr';
  const candidates = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean);
  for (const tag of candidates) {
    const base = tag.toLowerCase().split('-')[0];
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(base)) {
      return base as SupportedLanguage;
    }
  }
  return 'fr';
}

export function getUiTranslations(language: string): UiTranslations {
  return ALL_TRANSLATIONS[language] || fr;
}
